import type { DtcSeverity } from '@fleetcore/shared/telemetry'
import { AlertOctagon, AlertTriangle, Info } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { Badge } from '../../components/ui/badge'
import { useFormatters } from '../../i18n/format'
import { cn } from '../../lib/cn'
import type { DtcEventView } from './api-types'
import { sortDtcEvents } from './dtc-model'

/**
 * The fault list.
 *
 * The rule the whole component is built around: an unknown code is displayed, not
 * hidden. `P1234` on an Iveco is manufacturer-specific and the shared dictionary
 * deliberately carries no generic description for it — so the row shows the mnemonic,
 * says the description is unknown, and keeps the severity the dictionary assigns to an
 * unnamed fault, which is `warning` and not `info`. A row that vanished because we
 * could not translate it is a fault the workshop never hears about.
 *
 * The raw token is shown too whenever it differs from the normalised code. A device
 * emitting `p0301 ` or `P03O1` is itself the fault being reported, and a column that
 * only ever holds the cleaned-up form cannot evidence that.
 */

const SEVERITY_TONE: Record<DtcSeverity, 'danger' | 'warning' | 'info'> = {
  critical: 'danger',
  warning: 'warning',
  info: 'info',
}

const SEVERITY_ICON: Record<DtcSeverity, LucideIcon> = {
  critical: AlertOctagon,
  warning: AlertTriangle,
  info: Info,
}

export function DtcSeverityBadge({ severity }: { severity: DtcSeverity }) {
  const { t } = useTranslation()
  const Icon = SEVERITY_ICON[severity] ?? AlertTriangle
  return (
    <Badge tone={SEVERITY_TONE[severity] ?? 'warning'}>
      <Icon aria-hidden className="h-3 w-3" />
      {t(`diagnostics:severity.${severity}`)}
    </Badge>
  )
}

export interface DtcListProps {
  events: readonly DtcEventView[]
  /** Cleared faults show when they cleared; active ones show how long they have run. */
  variant?: 'active' | 'history'
  className?: string
}

export function DtcList({ events, variant = 'active', className }: DtcListProps) {
  const sorted = sortDtcEvents(events)

  return (
    <ul className={cn('divide-y divide-border', className)}>
      {sorted.map((event) => (
        <DtcRow key={event.id} event={event} variant={variant} />
      ))}
    </ul>
  )
}

function DtcRow({ event, variant }: { event: DtcEventView; variant: 'active' | 'history' }) {
  const { t } = useTranslation()
  const formatters = useFormatters()
  const rawDiffers = event.codeRaw.trim().toUpperCase() !== event.code

  return (
    <li className="flex flex-wrap items-start gap-x-4 gap-y-2 py-3">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <code className="rounded bg-surface-muted px-1.5 py-0.5 font-mono text-sm font-semibold text-foreground">
            {event.code}
          </code>
          <DtcSeverityBadge severity={event.severity} />
          {event.system !== null && event.systemLabel !== null ? (
            <Badge tone="neutral">{event.systemLabel}</Badge>
          ) : null}
          {event.malformed ? <Badge tone="warning">{t('diagnostics:dtc.malformed')}</Badge> : null}
        </div>

        <p
          className={cn(
            'mt-1 text-sm leading-relaxed',
            event.descriptionKnown ? 'text-foreground' : 'italic text-foreground-muted',
          )}
        >
          {/* Rule 5: never swallow a fault because it is not in the dictionary. */}
          {event.description ??
            t(
              event.manufacturerSpecific
                ? 'diagnostics:dtc.descriptionManufacturer'
                : 'diagnostics:dtc.descriptionUnknown',
            )}
        </p>

        {/*
          The subsystem is read off SAE's numbering, not looked up, so it survives a
          code we cannot name — and that is precisely when it is worth printing. It is
          shown for known codes too: on a list of nine faults, "Transmission" three
          times over is the pattern, and the pattern is the diagnosis.
        */}
        {event.family === null ? null : (
          <p className="mt-1 text-xs text-foreground-subtle">
            {t('diagnostics:dtc.familyLabel')} · {event.family}
          </p>
        )}

        {rawDiffers ? (
          <p className="mt-1 font-mono text-xs text-foreground-subtle">
            {t('diagnostics:dtc.rawToken', { raw: event.codeRaw })}
          </p>
        ) : null}
      </div>

      <dl className="shrink-0 space-y-0.5 text-xs text-foreground-subtle">
        <div className="flex gap-1.5">
          <dt>{t('diagnostics:dtc.firstSeen')}</dt>
          <dd className="text-foreground-muted">{formatters.dateTime(event.firstSeenAt)}</dd>
        </div>
        <div className="flex gap-1.5">
          <dt>
            {t(variant === 'history' ? 'diagnostics:dtc.cleared' : 'diagnostics:dtc.lastSeen')}
          </dt>
          <dd className="text-foreground-muted">
            {variant === 'history' && event.clearedAt !== null
              ? formatters.dateTime(event.clearedAt)
              : formatters.dateTime(event.lastSeenAt)}
          </dd>
        </div>
      </dl>
    </li>
  )
}
